import { AlertCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Loader2, Shield, RefreshCw, LayoutGrid, List } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useToastStore } from "../../stores/toastStore";
import { Tip } from "./ui/tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

/* ── Cell that lazily shows the themed tooltip only when its content overflows ── */
function OverflowCell({
  tooltip,
  clampClass,
  children,
}: {
  tooltip?: string;
  clampClass: string;
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const triggerCls = `block w-full break-words break-all ${clampClass}`;

  if (!tooltip) {
    return clampClass ? (
      <span className={triggerCls}>{children}</span>
    ) : (
      <>{children}</>
    );
  }

  const isOverflowing = () => {
    const el = triggerRef.current;
    if (!el) return false;
    return (
      el.scrollWidth > el.clientWidth + 1 ||
      el.scrollHeight > el.clientHeight + 1
    );
  };

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => setOpen(next ? isOverflowing() : false)}
    >
      <TooltipTrigger asChild>
        <span ref={triggerRef} className={`${triggerCls}`}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="max-w-md text-left whitespace-pre-wrap break-words"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

/* ── Sort-indicator glyphs (kept as escapes, not raw symbols) ── */
const SORT_ARROW_UP = String.fromCharCode(0x25B2); // Black Up-Pointing Triangle
const SORT_ARROW_DOWN = String.fromCharCode(0x25BC); // Black Down-Pointing Triangle

/* ── Column definition ──────────────────────────────────── */
export interface Column<T> {
  /** Unique key (used as React key and to auto-read row[key] when render is omitted) */
  key: string;
  /** Header label */
  header: ReactNode;
  /** Render cell content. When omitted, displays `String(row[key])`. */
  render?: (row: T, index: number) => ReactNode;
  /** Text alignment - defaults to 'center' */
  align?: "left" | "center" | "right";
  /** Header alignment - defaults to `align`. Set to center a header while the cells stay left/right. */
  headerAlign?: "left" | "center" | "right";
  /** Deprecated: column widths are now content-driven. Kept for back-compat — ignored at render time. */
  width?: string;
  /** Optional extra className for the header cell */
  headerClassName?: string;
  /** Optional extra className for every body cell in this column */
  cellClassName?: string;
  /** Enable sorting on this column */
  sortable?: boolean;
  /**
   * Truncate cell text with ellipsis when content overflows.
   * - `true` → single-line truncate (`truncate`)
   * - `1 | 2 | 3` → multi-line clamp (`line-clamp-N`)
   * Pair with `tooltip` to show full text on hover.
   */
  ellipsis?: boolean | 1 | 2 | 3;
  /** Tooltip text for the cell (HTML title attribute). Most useful when `ellipsis` is set. */
  tooltip?: (row: T) => string | undefined;
}

/* ── Props ───────────────────────────────────────────────── */
export interface DataTableProps<T> {
  columns: Column<T>[];
  /** Rows to render (already sliced for current page if you paginate externally) */
  data: T[];

  /** Show loading skeleton */
  loading?: boolean;
  /** Error message - shown in place of rows */
  error?: string | null;
  /** Retry callback shown alongside error */
  onRetry?: () => void;

  /** Empty state text */
  emptyText?: string;
  /** Custom empty state icon (defaults to Shield) */
  emptyIcon?: ReactNode;

  /** Row click handler */
  onRowClick?: (row: T, index: number) => void;
  /** Extract a unique key per row - falls back to index */
  rowKey?: (row: T, index: number) => string | number;
  /** Legacy: field name used as row key */
  keyField?: keyof T;
  /** Legacy: alternative empty message */
  emptyMessage?: string;
  /** Legacy: compact mode (smaller padding) */
  compact?: boolean;

  /**
   * Optional max height for the scroll viewport (e.g. `"60vh"`, `"480px"`).
   * When set, the body scrolls vertically and the header stays sticky.
   */
  maxHeight?: string;

  /**
   * Optional minimum width for the table (e.g. `"1100px"`). When the viewport
   * is narrower than this, the table keeps its column widths and the container
   * scrolls horizontally instead of compressing/wrapping columns.
   */
  minTableWidth?: string;

  /* ── Pagination (all optional - omit to hide) ── */
  page?: number;
  pageSize?: number;
  /** Total items across all pages (needed for "Showing X of Y") */
  totalItems?: number;
  onPageChange?: (page: number) => void;
  /** Noun shown in "Showing 1–10 of 42 {label}" - defaults to "items" */
  itemLabel?: string;
  /** Hide the prev/next/page-number buttons while keeping the "Showing X-Y of N" label. */
  hidePageButtons?: boolean;
  /** Optional per-row className */
  rowClassName?: (row: T, index: number) => string;
  /** Optional themed tooltip text per row — e.g. to hint that a row is clickable */
  rowTooltip?: (row: T, index: number) => string | undefined;

  /* ── Sorting (all optional) ── */
  /** Currently sorted column key */
  sortKey?: string | null;
  /** Current sort direction */
  sortDir?: "asc" | "desc";
  /** Called when a sortable column header is clicked */
  onSort?: (key: string) => void;
  sortIcon?: "chevron" | "arrows";

  /* ── View mode (Card / Table) ── */
  /**
   * When true, a Card/Table view toggle is shown above the table. In card
   * mode, rows are rendered as a responsive card grid derived from the
   * column definitions (first column = card title, others = label/value pairs).
   */
  enableCardView?: boolean;
  /** Initial view mode when `enableCardView` is true. Defaults to `"card"`. */
  defaultViewMode?: "card" | "table";
  /**
   * Controlled view mode. When provided, DataTable uses this value and skips
   * rendering its own internal toggle strip — the parent page is expected to
   * render its own `<ViewModeToggle>` (typically inline with a page-owned
   * toolbar next to a "Rows per page" dropdown).
   */
  viewMode?: "card" | "table";
  /** Called when the user flips the controlled view mode via the exposed toggle. */
  onViewModeChange?: (v: "card" | "table") => void;
  /** Custom card renderer. When provided, replaces the auto-generated card grid. */
  renderCard?: (items: T[]) => ReactNode;
  /** Keys of columns to exclude from auto-card view (e.g. a "#" serial column). */
  cardExcludeKeys?: string[];
}

/* ── View-mode toggle (Card / Table) ── */
export function ViewModeToggle({
  value,
  onChange,
}: {
  value: "card" | "table";
  onChange: (v: "card" | "table") => void;
}) {
  const btn = (mode: "card" | "table", label: string, icon: ReactNode) => {
    const active = value === mode;
    return (
      <Tip label={label}>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={() => onChange(mode)}
          className={`flex size-8 items-center justify-center rounded-md transition-colors ${
            active
              ? "bg-white dark:bg-white/10 text-indigo-600 dark:text-indigo-400 shadow-sm"
              : "text-[#64748b] dark:text-white/60 hover:text-foreground"
          }`}
        >
          {icon}
        </button>
      </Tip>
    );
  };
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
      {btn("card", "Card view", <LayoutGrid className="size-4" />)}
      {btn("table", "Table view", <List className="size-4" />)}
    </div>
  );
}

/* ── Auto-generated card grid from column defs ── */
function AutoCardGrid<T>({
  rows,
  columns,
  onRowClick,
  rowKey,
  excludeKeys,
}: {
  rows: T[];
  columns: Column<T>[];
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T, index: number) => string | number;
  excludeKeys?: string[];
}) {
  const visible = columns.filter(
    (c) =>
      !excludeKeys?.includes(c.key) &&
      c.key !== "no" &&
      c.key !== "sno" &&
      c.header !== "#",
  );
  const titleCol = visible[0];
  const detailCols = visible.slice(1);

  const extractHeaderText = (header: ReactNode): string => {
    if (typeof header === "string") return header;
    if (typeof header === "number") return String(header);
    return "";
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-1">
      {rows.map((row, idx) => {
        const k = rowKey ? rowKey(row, idx) : idx;
        const titleContent = titleCol?.render
          ? titleCol.render(row, idx)
          : String((row as Record<string, unknown>)[titleCol?.key ?? ""] ?? "");
        return (
          <div
            key={k}
            onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
            role={onRowClick ? "button" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(row, idx);
                    }
                  }
                : undefined
            }
            className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:shadow-[0_12px_28px_-8px_rgba(79,70,229,0.28)] dark:hover:shadow-[0_0_28px_rgba(79,70,229,0.32)] ${onRowClick ? "cursor-pointer" : ""} outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
          >
            <div className="relative z-[1] flex flex-1 flex-col gap-3 p-4">
              {titleCol && (
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {titleContent}
                  </div>
                </div>
              )}
              {detailCols.length > 0 && (
                <div className="flex flex-col gap-2 mt-1">
                  {detailCols.map((col) => {
                    const val = col.render
                      ? col.render(row, idx)
                      : String((row as Record<string, unknown>)[col.key] ?? "");
                    const label = extractHeaderText(col.header);
                    return (
                      <div
                        key={col.key}
                        className="flex items-start justify-between gap-3 text-[12px]"
                      >
                        {label && (
                          <span className="font-semibold text-[#64748b] dark:text-white/60 whitespace-nowrap">
                            {label}
                          </span>
                        )}
                        <div
                          className="flex-1 min-w-0 text-right text-[#0f172a] dark:text-white/85"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {val}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Alignment helper ───────────────────────────────────── */
const alignCls = (a?: "left" | "center" | "right") =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";

const ellipsisCls = (e: Column<unknown>["ellipsis"]) => {
  if (e === true) return "truncate";
  if (e === 1) return "line-clamp-1";
  if (e === 2) return "line-clamp-2";
  if (e === 3) return "line-clamp-3";
  return "";
};

/* ── Pagination window helper ───────────────────────────── */
/** Build a compact page list: [1, …, current-2 … current+2, …, last]. `page` is 0-indexed.
 *  Shows up to `SIBLINGS` pages on each side of the current page so navigation to
 *  higher/lower pages stays visible even with large totals. */
const SIBLINGS = 2;
const getPageWindow = (
  page: number,
  totalPages: number,
): (number | "dots")[] => {
  // Everything fits without ellipsis: 1 + (2*SIBLINGS+1) + 1 first/last + 2 dot slots
  const maxVisible = 2 * SIBLINGS + 5;
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const current = page + 1;
  const out: (number | "dots")[] = [1];
  const start = Math.max(2, current - SIBLINGS);
  const end = Math.min(totalPages - 1, current + SIBLINGS);
  if (start > 2) out.push("dots");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < totalPages - 1) out.push("dots");
  out.push(totalPages);
  return out;
};

/* ── Component ──────────────────────────────────────────── */
export function DataTable<T>({
  columns,
  data,
  loading = false,
  error = null,
  onRetry,
  emptyText: emptyTextProp,
  emptyMessage,
  emptyIcon,
  onRowClick,
  rowKey,
  keyField,
  compact = false,
  maxHeight,
  minTableWidth,
  page,
  pageSize = 10,
  totalItems,
  onPageChange,
  itemLabel = "items",
  hidePageButtons = false,
  rowClassName,
  rowTooltip,
  sortKey,
  sortDir = "asc",
  onSort,
  sortIcon = "chevron",
  enableCardView = false,
  defaultViewMode = "card",
  viewMode: viewModeProp,
  onViewModeChange,
  renderCard,
  cardExcludeKeys,
}: DataTableProps<T>) {
  const emptyText = emptyTextProp ?? emptyMessage ?? "No data available";
  const getRowKey = rowKey
    ? rowKey
    : keyField
      ? (row: T) => String((row as Record<string, unknown>)[String(keyField)])
      : undefined;

  const [internalViewMode, setInternalViewMode] = useState<"card" | "table">(
    defaultViewMode,
  );
  const isControlled = viewModeProp !== undefined;
  const viewMode = isControlled ? viewModeProp : internalViewMode;
  const setViewMode = (v: "card" | "table") => {
    if (!isControlled) setInternalViewMode(v);
    onViewModeChange?.(v);
  };
  const canToggleView = enableCardView || !!renderCard;
  const activeView: "card" | "table" = canToggleView ? viewMode : "table";
  // When controlled, the page renders its own toggle in its toolbar.
  const showInternalToggle = canToggleView && !isControlled;

  const colCount = columns.length;
  const total = totalItems ?? data.length;
  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 1;
  const hasPagination =
    page !== undefined && onPageChange !== undefined && total > 0;
  // Horizontal padding must match between <th> and <td> for columns to line
  // up — only the vertical padding is allowed to differ between compact and
  // regular density.
  const cellPadX = compact ? "px-4" : "px-5";
  const cellPad = compact ? `${cellPadX} py-2.5` : `${cellPadX} py-4`;

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  return (
    // `flex flex-col min-h-0 flex-1` lets the table claim remaining height when
    // the parent is a flex column with a constrained height (e.g. PageShell with
    // h-[calc(100vh-64px)] flex flex-col). That keeps scrolling *inside* the
    // table — single inline scrollbar with the sticky header pinned — instead
    // of the page scrolling around it.
    <div className="flex flex-col min-h-0 flex-1 gap-2">
      {showInternalToggle && (
        <div className="flex-shrink-0 flex items-center justify-end">
          <ViewModeToggle value={activeView} onChange={setViewMode} />
        </div>
      )}
      {/* ── Table card ─────────────────────────────────── */}
      <div
        className={
          activeView === "card"
            ? "w-full flex flex-col min-h-0 flex-1"
            : "w-full rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] shadow-sm overflow-hidden flex flex-col min-h-0 flex-1"
        }
      >
        {activeView === "card" ? (
          <div
            className="w-full overflow-auto flex-1 min-h-0"
            style={{ maxHeight: maxHeight ?? "calc(100vh - 280px)" }}
          >
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-2xl border-2 border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-3.5 flex flex-col gap-3"
                  >
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="h-3.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-3/4" />
                      <div className="size-4 rounded bg-[#e2e8f0] dark:bg-white/[0.09]" />
                    </div>
                    <div className="flex gap-1.5">
                      <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-16" />
                      <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-10" />
                    </div>
                    <div className="mt-auto flex flex-col gap-3">
                      <div className="h-14 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#edf0f4] dark:border-white/[0.07]" />
                      <div className="flex flex-col gap-1.5">
                        <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-2/3" />
                        <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-1/2" />
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                        <div className="h-4 rounded-full bg-[#e2e8f0] dark:bg-white/[0.09] w-20" />
                        <div className="flex gap-1">
                          <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                          <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div
                role="alert"
                className="flex flex-col items-center justify-center gap-3 py-16 rounded-2xl border border-dashed border-red-200 dark:border-red-500/30 bg-red-50/40 dark:bg-red-500/[0.05]"
              >
                <div className="size-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex flex-col items-center gap-1 max-w-md text-center px-4">
                  <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
                    Something went wrong.
                  </p>
                  <p className="text-[12px] text-[#64748b] dark:text-white/60 break-words">
                    {error}
                  </p>
                </div>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 bg-white dark:bg-white/[0.04] text-[12px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10 transition-colors"
                  >
                    <RefreshCw className="size-3.5" />
                    Retry
                  </button>
                )}
              </div>
            ) : data.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16">
                <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                  {emptyIcon ?? (
                    <Shield className="size-4 text-[#64748b] dark:text-white/50" />
                  )}
                </div>
                <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
                  {emptyText}
                </p>
              </div>
            ) : renderCard ? (
              renderCard(data)
            ) : (
              <AutoCardGrid
                rows={data}
                columns={columns}
                onRowClick={onRowClick}
                rowKey={getRowKey}
                excludeKeys={cardExcludeKeys}
              />
            )}
          </div>
        ) : (
        <div
          className="datatable-scroll w-full overflow-auto flex-1 min-h-0"
          // maxHeight is the fallback for non-flex parents; inside a flex parent
          // `flex-1 min-h-0` fills the available space (up to this cap).
          style={{ maxHeight: maxHeight ?? "calc(100vh - 280px)" }}
        >
          <table
            style={minTableWidth ? { minWidth: minTableWidth } : undefined}
            className="w-full min-w-full table-auto text-[13px] [&_th+th]:border-l [&_th+th]:border-[#e2e8f0] dark:[&_th+th]:border-white/[0.06] [&_td+td]:border-l [&_td+td]:border-[#e2e8f0] dark:[&_td+td]:border-white/[0.06]"
          >
            {/* Header — always sticky so it stays in place while rows scroll
                (sticky anchors to the nearest scroll ancestor: the inner viewport
                when `maxHeight` is set, otherwise the page). */}
            <thead className="sticky top-0 z-20 bg-[#eef2f7] dark:bg-[#1b1c2e]">
              <tr className="border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#eef2f7] dark:bg-white/[0.05]">
                {columns.map((col) => {
                  const isSortable = col.sortable && onSort;
                  const isActive = sortKey === col.key;
                  const headerAlign = col.headerAlign ?? col.align;
                  return (
                    <th
                      key={col.key}
                      className={`${cellPadX} py-3.5 text-[13px] font-medium text-[#1e293b] dark:text-white whitespace-nowrap ${alignCls(headerAlign)}
                      ${col.width ?? ""} ${col.headerClassName ?? ""} ${isSortable ? "cursor-pointer select-none hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors" : ""}`}
                      onClick={isSortable ? () => onSort(col.key) : undefined}
                    >
                      {isSortable ? (
                        (() => {
                          const icon =
                            sortIcon === "arrows" ? (
                              <span
                                className="inline-flex flex-col items-center select-none shrink-0"
                                style={{ fontSize: "7px", lineHeight: "7px" }}
                              >
                                <span
                                  className={`font-black ${isActive && sortDir === "asc" ? "text-indigo-600 dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                                >
                                  {SORT_ARROW_UP}
                                </span>
                                <span
                                  className={`font-black ${isActive && sortDir === "desc" ? "text-indigo-600 dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                                >
                                  {SORT_ARROW_DOWN}
                                </span>
                              </span>
                            ) : isActive ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="size-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                              ) : (
                                <ChevronDown className="size-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                              )
                            ) : (
                              <ChevronsUpDown className="size-3.5 shrink-0 text-[#94a3b8] dark:text-white/30" />
                            );
                          // The sort icon must not pull the label text off the
                          // alignment point the data cell below uses (which has
                          // no icon). Left-align is naturally fine (icon trails
                          // after, doesn't move the text's start). Right-align:
                          // put the icon BEFORE the text so the text itself (not
                          // the icon) touches the true right edge, same as the
                          // data cell. Center-align: mirror the icon's width as
                          // an invisible spacer on the opposite side so the text
                          // sits at the true center, not the icon+text group's
                          // center.
                          if (headerAlign === "right") {
                            return (
                              <span className="inline-flex items-center gap-1.5 justify-end">
                                {icon}
                                {col.header}
                              </span>
                            );
                          }
                          if (headerAlign === "left") {
                            return (
                              <span className="inline-flex items-center gap-1.5 justify-start">
                                {col.header}
                                {icon}
                              </span>
                            );
                          }
                          return (
                            <span className="flex items-center w-full gap-1.5">
                              <span className="shrink-0 invisible" aria-hidden="true">
                                {icon}
                              </span>
                              <span className="flex-1 text-center">
                                {col.header}
                              </span>
                              <span className="shrink-0">{icon}</span>
                            </span>
                          );
                        })()
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {/* Loading */}
              {loading ? (
                <tr>
                  <td colSpan={colCount} className="px-5 py-14 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
                      <span className="text-[13px] text-[#64748b] dark:text-white/50">
                        Loading…
                      </span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={colCount} className="px-5 py-14 text-center">
                    <div
                      role="alert"
                      className="flex flex-col items-center gap-3"
                    >
                      <div className="size-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                        <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex flex-col items-center gap-1 max-w-md">
                        <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
                          Something went wrong.
                        </p>
                        <p className="text-[12px] text-[#64748b] dark:text-white/60 break-words">
                          {error}
                        </p>
                      </div>
                      {onRetry && (
                        <button
                          onClick={onRetry}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 bg-white dark:bg-white/[0.04] text-[12px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10 transition-colors"
                        >
                          <RefreshCw className="size-3.5" />
                          Retry
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                /* Empty */
                <tr>
                  <td colSpan={colCount} className="px-5 py-14 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                        {emptyIcon ?? (
                          <Shield className="size-4 text-[#64748b] dark:text-white/50" />
                        )}
                      </div>
                      <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
                        {emptyText}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                /* Data rows */
                data.map((row, idx) => {
                  // Guard against callbacks that throw on a malformed row so a
                  // single bad row can't take down the whole table.
                  let rowK: string | number = idx;
                  try {
                    if (getRowKey) rowK = getRowKey(row, idx);
                  } catch {
                    rowK = idx;
                  }
                  let rowTip: string | undefined;
                  try {
                    rowTip = rowTooltip ? rowTooltip(row, idx) : undefined;
                  } catch {
                    rowTip = undefined;
                  }
                  const rowEl = (
                  <tr
                    key={rowK}
                    onClick={
                      onRowClick ? () => onRowClick(row, idx) : undefined
                    }
                    className={`border-b border-[#e2e8f0] dark:border-white/[0.06] last:border-0 transition-[background-color,box-shadow] duration-150 ease-out group
                    ${onRowClick ? "cursor-pointer hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]" : ""}
                    ${rowClassName ? rowClassName(row, idx) : ""}`}
                  >
                    {columns.map((col) => {
                      const val = (row as Record<string, unknown>)[
                        String(col.key)
                      ];
                      const content = col.render
                        ? col.render(row, idx)
                        : String(val ?? "");
                      const tip = col.tooltip?.(row);
                      const clamp = ellipsisCls(col.ellipsis);
                      const alignWrap =
                        col.align === "right"
                          ? "flex justify-end"
                          : col.align === "left"
                            ? "flex justify-start"
                            : "flex justify-center";
                     
                      // Serial / index columns must never wrap — the global
                      // word-break rule otherwise splits short numbers like
                      // "10" / "20" across two lines in a narrow column. The
                      // header may be a plain "#" string or a "#" wrapped in an
                      // element, and the key varies (idx/index/sno/no/serial).
                      const headerChildren =
                        col.header &&
                        typeof col.header === "object" &&
                        "props" in col.header
                          ? (col.header as { props?: { children?: unknown } })
                              .props?.children
                          : undefined;
                      const headerIsHash =
                        col.header === "#" || headerChildren === "#";
                      const keyLc = String(col.key).toLowerCase();
                      const isSerialCol =
                        headerIsHash ||
                        keyLc.endsWith("sno") ||
                        ["idx", "index", "sno", "serial", "no", "#"].includes(
                          keyLc,
                        );
                      return (
                        <td
                          key={String(col.key)}
                          className={`${cellPad} ${alignCls(col.align)} text-[#0f172a] dark:text-white/80 align-top max-w-xs ${isSerialCol ? "whitespace-nowrap" : "whitespace-normal break-words"} ${col.cellClassName ?? ""}`}
                        >
                          <div className={alignWrap}>
                            <OverflowCell tooltip={tip} clampClass={clamp}>
                              {content}
                            </OverflowCell>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  );
                  return rowTip ? (
                    <Tooltip key={rowK}>
                      <TooltipTrigger asChild>{rowEl}</TooltipTrigger>
                      <TooltipContent side="top" sideOffset={6}>
                        {rowTip}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    rowEl
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* ── Pagination — sits inside the card as a compact footer ── */}
        {hasPagination && (
          <div
            className={`flex items-center justify-between gap-2 px-4 py-2 flex-shrink-0 bg-[#fafbfc] dark:bg-white/[0.015] ${
              activeView === "card"
                ? "rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] mt-2"
                : "border-t border-[#e2e8f0] dark:border-white/[0.06]"
            }`}
          >
            <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
              Showing{" "}
              <span className="font-semibold text-[#334155] dark:text-white/90">
                {page! * pageSize + 1}–{Math.min((page! + 1) * pageSize, total)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-[#334155] dark:text-white/90">
                {total}
              </span>{" "}
              {itemLabel}
            </p>

            {!hidePageButtons && totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button
                  disabled={page === 0}
                  onClick={() => onPageChange!(page! - 1)}
                  className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronLeft className="size-4" />
                </button>

                {getPageWindow(page!, totalPages).map((p, idx) =>
                  p === "dots" ? (
                    <span
                      key={`dots-${idx}`}
                      className="flex size-8 items-center justify-center text-xs text-[#94a3b8] dark:text-white/40 select-none"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => onPageChange!(p - 1)}
                      className={`flex size-8 items-center justify-center text-xs font-semibold rounded-lg border transition-colors ${
                        p - 1 === page
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                          : "border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  disabled={page! >= totalPages - 1}
                  onClick={() => onPageChange!(page! + 1)}
                  className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
